interface InkPageProps {
  onNavigate: (page: string, data?: any) => void;
  user: any;
}

const INK_REWARDS = [
  { action: 'New account signup', amount: 500, icon: '🎉', category: 'Getting Started' },
  { action: 'Publish a new chapter', amount: 25, icon: '📝', category: 'Writing' },
  { action: 'Receive a new follower on your story', amount: 10, icon: '👥', category: 'Writing' },
  { action: 'Receive a review on your story', amount: 15, icon: '⭐', category: 'Writing' },
  { action: 'Receive a tip from a reader', amount: '10 / 50 / 100', icon: '💝', category: 'Tips' },
  { action: 'Win 1st place in a contest', amount: 500, icon: '🥇', category: 'Contests' },
  { action: 'Win 2nd place in a contest', amount: 250, icon: '🥈', category: 'Contests' },
  { action: 'Win 3rd place in a contest', amount: 100, icon: '🥉', category: 'Contests' },
];

const INK_USES = [
  { action: 'Tip an author (10 Ink)', description: 'Show small appreciation for a story you enjoy' },
  { action: 'Tip an author (50 Ink)', description: 'A meaningful gesture of support for great writing' },
  { action: 'Tip an author (100 Ink)', description: 'A big thank you for an outstanding story' },
];

export default function InkPage({ onNavigate, user }: InkPageProps) {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-900/30 mb-6">
          <span className="text-4xl">✦</span>
        </div>
        <h1 className="text-4xl font-bold mb-4">
          What is <span className="text-amber-400">Ink</span>?
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Ink is ScribeHaven's virtual currency. Authors earn Ink by writing and growing their audience.
          Readers use Ink to tip their favorite authors and show appreciation for great stories.
        </p>
        {user && (
          <div className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-gray-900 border border-gray-700 rounded-xl">
            <span className="text-amber-400 text-xl">✦</span>
            <span className="text-2xl font-bold text-amber-400">{user.coinsBalance?.toLocaleString() ?? 0}</span>
            <span className="text-gray-400">Ink</span>
          </div>
        )}
      </div>

      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-6">How to Earn Ink</h2>
        <div className="space-y-3">
          {['Getting Started', 'Writing', 'Tips', 'Contests'].map(category => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 mt-4">{category}</h3>
              {INK_REWARDS.filter(r => r.category === category).map((reward, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-900 border border-gray-800 rounded-xl mb-2">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">{reward.icon}</span>
                    <span className="text-gray-200">{reward.action}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-amber-400">✦</span>
                    <span className="text-amber-400 font-bold">
                      {typeof reward.amount === 'number' ? `+${reward.amount}` : reward.amount}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-6">How to Use Ink</h2>
        <div className="space-y-3">
          {INK_USES.map((use, i) => (
            <div key={i} className="p-4 bg-gray-900 border border-gray-800 rounded-xl">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-amber-400">✦</span>
                <span className="font-medium text-gray-200">{use.action}</span>
              </div>
              <p className="text-gray-500 text-sm ml-8">{use.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-br from-amber-900/20 to-ink-900/20 border border-amber-800/30 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold mb-3">Ready to start earning?</h2>
        <p className="text-gray-400 mb-6">Write stories, grow your audience, and watch your Ink balance grow.</p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => onNavigate('dashboard')}
            className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
          >
            Start Writing
          </button>
          <button
            onClick={() => onNavigate('discover')}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold transition-colors"
          >
            Discover Stories
          </button>
        </div>
      </div>
    </div>
  );
}
